import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { downloadUrlAsFile } from "@/lib/download";
import { Sparkles, ImageIcon, Info, Loader2, Download, Maximize, ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { imageModelsForSize, DEFAULT_IMAGE_MODEL } from "@/lib/aiModels";
import { useGenerationCosts, formatActionKey, FALLBACK_FORMAT_COST } from "@/hooks/useGenerationCosts";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { FormatEditLightbox } from "@/components/app/DeliverableEditLightbox";

import { supabase, getAuthHeader } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import * as Sentry from "@sentry/react";

export interface FormatDefinition {
  ratio: string;
  width: number;
  height: number;
  platforms: string[];
  description: string;
  credits: number;
  auto?: boolean;
}

export const FORMAT_DEFINITIONS: FormatDefinition[] = [
  { ratio: "1:1", width: 1080, height: 1080, platforms: ["Meta feed", "LinkedIn feed", "Google Display", "TikTok feed"], description: "Universelt kvadratformat. Trygt valg for feed-annonser på tvers av plattformer.", credits: 15 },
  { ratio: "4:5", width: 1080, height: 1350, platforms: ["Meta feed", "LinkedIn"], description: "Tar mer plass i feeden enn 1:1. Anbefalt for Instagram og Facebook feed.", credits: 18 },
  { ratio: "2:3", width: 1000, height: 1500, platforms: ["Pinterest", "Meta feed"], description: "Standard Pinterest-pin. Fungerer i portrett-feed.", credits: 18 },
  { ratio: "3:4", width: 1080, height: 1440, platforms: ["Meta feed", "Snapchat"], description: "Portrett feed-format. Ligner 4:5 men noe kortere.", credits: 18 },
  { ratio: "9:16", width: 1080, height: 1920, platforms: ["TikTok", "Reels / Stories", "Snapchat", "YouTube Shorts"], description: "Fullskjerm vertikal video/bilde. Viktigste format for mobil-first annonsering.", credits: 25 },
  { ratio: "1:4", width: 300, height: 1200, platforms: ["Google Display", "Programmatic"], description: "Skyscraper-format. Sidekolonne på desktop-nettsider.", credits: 12 },
  { ratio: "16:9", width: 1920, height: 1080, platforms: ["YouTube pre-roll", "Meta video", "LinkedIn video", "Google Display"], description: "Standard widescreen. Viktigste format for videoannonsering.", credits: 20 },
  { ratio: "3:2", width: 1080, height: 720, platforms: ["Meta feed", "LinkedIn"], description: "Ligner klassisk fotoproporsjoner. Brukt i feed der horisontal fremstilling passer.", credits: 15 },
  { ratio: "4:3", width: 1200, height: 900, platforms: ["Google Display", "Programmatic"], description: "Klassisk display-banner. Brukes i rectangle-formater.", credits: 15 },
  { ratio: "5:4", width: 1280, height: 1024, platforms: ["Google Display", "Programmatic"], description: "Passer noen display-nettverk og digitale skjermer (DOOH).", credits: 15 },
  { ratio: "21:9", width: 2560, height: 1080, platforms: ["YouTube masthead", "DOOH", "Programmatic"], description: "Ultrawide. Brukt i premium display og digital utendørs.", credits: 22 },
  { ratio: "4:1", width: 1200, height: 300, platforms: ["Google Display", "LinkedIn leaderboard", "Programmatic"], description: "Leaderboard-banner. Topp/bunn av nettsider og LinkedIn desktop.", credits: 12 },
  { ratio: "8:1", width: 1200, height: 150, platforms: ["Google Display", "Programmatic"], description: "Super-leaderboard / billboard. Bred toppbanner på desktop-sider.", credits: 10 },
  { ratio: "1:8", width: 160, height: 1280, platforms: ["Google Display", "Programmatic"], description: "Smal wide skyscraper. Sidekolonne på desktop.", credits: 10 },
  { ratio: "Auto", width: 0, height: 0, platforms: ["Google Performance Max", "Meta Advantage+"], description: "Plattformen beskjærer og tilpasser automatisk. Last opp høyest mulig oppløsning.", credits: 20, auto: true },
  // Upscale tiers — same 16:9 aspect ratio at higher resolutions
  { ratio: "2K", width: 2560, height: 1440, platforms: ["Print", "High-res web", "QHD displays"], description: "Oppskalering til 2K/QHD (2560×1440). Samme sideforhold, høyere oppløsning.", credits: 20 },
  { ratio: "4K", width: 3840, height: 2160, platforms: ["Print", "DOOH", "4K/UHD displays"], description: "Oppskalering til 4K/UHD (3840×2160). Profesjonell print og storformat.", credits: 40 },
];

/** Check if a format is an upscale tier */
// 2K and 4K only: they are the sizes Gemini can produce. 3K and 5K used to be
// tiles too — priced at 15 and 25 — but anything above 2048px renders at 4K,
// so both delivered the identical 4K image the 4K tile sells for 20. Selling a
// size that does not exist, at two different wrong prices.
export const isUpscaleFormat = (ratio: string) => ["2K", "4K"].includes(ratio);

const GROUPS: { label: string; ratios: string[] }[] = [
  { label: "Feed", ratios: ["1:1", "4:5", "2:3", "3:4", "3:2"] },
  { label: "Stories & Reels", ratios: ["9:16"] },
  { label: "Video & Widescreen", ratios: ["16:9", "21:9"] },
  { label: "Display & Banners", ratios: ["4:3", "5:4", "4:1", "8:1", "1:4", "1:8"] },
  { label: "Upscale", ratios: ["2K", "4K"] },
  { label: "Auto / Smart", ratios: ["Auto"] },
];

/** DB record shape for a format variant */
interface VariantRecord {
  id: string;
  format_key: string;
  asset_path: string;
  publicUrl: string;
}

interface FormatVariantsPanelProps {
  sourceImageUrl: string;
  productImageUrls?: string[];
  renderId?: string;
  workspaceId?: string;
  userId?: string;
  productInfo?: { name: string; brand?: string; category_key?: string; mounting_type?: string };
}

/** Downscale an image to exact target dimensions if it overshoots */
async function downscaleIfNeeded(imageUrl: string, targetWidth: number, targetHeight: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Only downscale if image is larger than target
      if (img.naturalWidth <= targetWidth && img.naturalHeight <= targetHeight) {
        // Already at or below target — return as-is
        if (imageUrl.startsWith("data:")) {
          const base64Part = imageUrl.split(",")[1];
          const byteArray = Uint8Array.from(atob(base64Part), (c) => c.charCodeAt(0));
          resolve(new Blob([byteArray], { type: "image/png" }));
        } else {
          fetch(imageUrl).then(r => r.blob()).then(resolve).catch(reject);
        }
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob failed"));
      }, "image/png");
    };
    img.onerror = () => reject(new Error("Failed to load image for downscale"));
    img.src = imageUrl;
  });
}

async function uploadBase64ToStorage(base64Url: string, storagePath: string, targetWidth?: number, targetHeight?: number): Promise<string> {
  let blob: Blob;

  if (targetWidth && targetHeight && targetWidth > 0 && targetHeight > 0) {
    blob = await downscaleIfNeeded(base64Url, targetWidth, targetHeight);
  } else if (base64Url.startsWith("data:")) {
    const base64Part = base64Url.split(",")[1];
    const byteArray = Uint8Array.from(atob(base64Part), (c) => c.charCodeAt(0));
    blob = new Blob([byteArray], { type: "image/png" });
  } else {
    const resp = await fetch(base64Url);
    blob = await resp.blob();
  }

  const { error } = await supabase.storage.from("renders").upload(storagePath, blob, { contentType: "image/png", upsert: true });
  if (error) {
    console.error("Storage upload error:", error);
    throw error;
  }
  const { data } = supabase.storage.from("renders").getPublicUrl(storagePath);
  return data.publicUrl;
}

export function FormatVariantsPanel({ sourceImageUrl, productImageUrls, renderId, workspaceId, userId, productInfo }: FormatVariantsPanelProps) {
  const [activeGroup, setActiveGroup] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Map format_key -> { id (DB row id), publicUrl }
  const [variants, setVariants] = useState<Map<string, VariantRecord>>(new Map());
  const [generating, setGenerating] = useState<Set<string>>(new Set());
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxRatio, setLightboxRatio] = useState<string | null>(null);
  const [lightboxVariantId, setLightboxVariantId] = useState<string | null>(null);
  const [lightboxFormat, setLightboxFormat] = useState<FormatDefinition | null>(null);

  const currentGroup = GROUPS[activeGroup];
  const groupFormats = FORMAT_DEFINITIONS.filter((f) => currentGroup.ratios.includes(f.ratio));

  // Load existing variants from DB on mount
  useEffect(() => {
    if (!renderId) return;
    (async () => {
      const { data, error } = await supabase
        .from("deliverables")
        .select("id, format_key, asset_path, status")
        .eq("visual_id", renderId)
        .eq("status", "complete");
      if (error) { console.error("Failed to load format variants:", error); return; }
      const map = new Map<string, VariantRecord>();
      for (const row of data || []) {
        if (row.asset_path) {
          const { data: urlData } = supabase.storage.from("renders").getPublicUrl(row.asset_path);
          map.set(row.format_key, {
            id: row.id,
            format_key: row.format_key,
            asset_path: row.asset_path,
            publicUrl: urlData.publicUrl + "?t=" + Date.now(),
          });
        }
      }
      setVariants(map);
    })();
  }, [renderId]);

  const toggleSelect = (ratio: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ratio)) next.delete(ratio);
      else next.add(ratio);
      return next;
    });
  };

  // Format variants upscale beyond 1K, so Flash Lite (1K-only) is not offered.
  // Credit costs come from generation_costs so pricing is adjustable
  // without a deploy; the per-format literals below are display fallbacks.
  const { costOf } = useGenerationCosts();
  const creditsFor = (ratio: string) => costOf(formatActionKey(ratio), FALLBACK_FORMAT_COST);

  const modelOptions = imageModelsForSize("2K");
  const [imageModel, setImageModel] = useState<string>(DEFAULT_IMAGE_MODEL);
  const selectedModel = modelOptions.find((m) => m.id === imageModel) || modelOptions[0];

  const handleGenerate = async (format: FormatDefinition) => {
    if (format.auto) {
      return;
    }

    const isUpscale = isUpscaleFormat(format.ratio);

    setGenerating((prev) => new Set(prev).add(format.ratio));
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-format-variant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authHeader,
          },
          body: JSON.stringify({
            sourceImageUrl,
            targetRatio: isUpscale ? "upscale" : format.ratio,
            targetWidth: format.width,
            targetHeight: format.height,
            productImageUrls: productImageUrls || [],
            upscaleLabel: isUpscale ? format.ratio : undefined,
            productInfo: productInfo || undefined,
            workspace_id: workspaceId,
            model: imageModel,
          }),
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Generation failed");
      }

      const data = await response.json();
      if (data.image) {
        // Save to storage + DB if we have context
        if (renderId && workspaceId && userId) {
          const variantId = crypto.randomUUID();
          const formatSafe = format.ratio.replace(":", "x");
          const storagePath = `${userId}/formats/${renderId}/${formatSafe}-${variantId}.png`;

          const publicUrl = await uploadBase64ToStorage(data.image, storagePath, format.width, format.height);

          const { data: dbRow, error: dbErr } = await supabase
            .from("deliverables")
            .insert({
              id: variantId,
              visual_id: renderId,
              workspace_id: workspaceId,
              format_key: format.ratio,
              asset_path: storagePath,
              status: "complete",
              credits_charged: creditsFor(format.ratio),
            } as any)
            .select()
            .single();

          if (dbErr) console.error("DB insert error:", dbErr);

          setVariants((prev) => new Map(prev).set(format.ratio, {
            id: variantId,
            format_key: format.ratio,
            asset_path: storagePath,
            publicUrl,
          }));
        } else {
          // Fallback: keep in memory only
          setVariants((prev) => new Map(prev).set(format.ratio, {
            id: crypto.randomUUID(),
            format_key: format.ratio,
            asset_path: "",
            publicUrl: data.image,
          }));
        }
      }
    } catch (error: any) {
      console.error("Format variant error:", error);
    } finally {
      setGenerating((prev) => {
        const next = new Set(prev);
        next.delete(format.ratio);
        return next;
      });
    }
  };

  const downloadVariant = (format: FormatDefinition, imageUrl: string) => {
    void downloadUrlAsFile(imageUrl, `format-${format.ratio.replace(":", "x")}-${format.width}x${format.height}.png`);
  };

  const handleDelete = async (ratio: string) => {
    const variant = variants.get(ratio);
    // Nothing to delete, but say so — returning silently here is
    // indistinguishable from a click that did not register.
    if (!variant) {
      console.warn("No generated variant for", ratio);
      return;
    }

    try {
      // The result is checked: an unchecked delete that fails looks exactly
      // like one that worked, because the row is dropped from local state
      // either way — and then reappears on the next load.
      if (variant.id) {
        const { data, error } = await supabase
          .from("deliverables")
          .delete()
          .eq("id", variant.id)
          .select("id");
        if (error) throw error;
        if (!data || data.length === 0) {
          throw new Error(`Format ${ratio} matched no rows on delete`);
        }
      }
      if (variant.asset_path) {
        const { error: rmErr } = await supabase.storage.from("renders").remove([variant.asset_path]);
        // An orphaned file is recoverable; a half-deleted record is not, so
        // this reports rather than aborting the delete.
        if (rmErr) {
          console.error("Failed to remove format file:", rmErr);
          Sentry.captureException(rmErr);
        }
      }

      setVariants((prev) => {
        const next = new Map(prev);
        next.delete(ratio);
        return next;
      });
    } catch (err: any) {
      console.error("Delete format failed:", err);
      Sentry.captureException(err);
      toast.error(`Could not delete the ${ratio} format: ${err?.message || "unknown error"}`);
    }
  };

  const handleImageEdited = async (ratio: string, newImageUrl: string) => {
    const variant = variants.get(ratio);
    if (!variant || !renderId || !workspaceId || !userId) {
      console.warn("handleImageEdited: missing context", { variant: !!variant, renderId, workspaceId, userId });
      return;
    }

    try {
      // Upload new image, reuse same storage path (upsert)
      const formatSafe = ratio.replace(":", "x");
      const storagePath = variant.asset_path || `${userId}/formats/${renderId}/${formatSafe}-${variant.id}.png`;
      console.log("Uploading edited format variant to:", storagePath);
      const publicUrl = await uploadBase64ToStorage(newImageUrl, storagePath);
      console.log("Upload complete, new URL:", publicUrl);

      // Update DB
      const { error: dbErr } = await supabase
        .from("deliverables")
        .update({ asset_path: storagePath } as any)
        .eq("id", variant.id);
      if (dbErr) console.error("DB update error:", dbErr);

      setVariants((prev) => new Map(prev).set(ratio, {
        ...variant,
        asset_path: storagePath,
        publicUrl: publicUrl + "?t=" + Date.now(), // cache bust
      }));
    } catch (err) {
      console.error("Failed to save edited variant:", err);
    }
  };

  return (
    <div className="border-t border-border bg-card/50">
      {/* Group tabs */}
      <div className="flex items-center gap-6 px-6 pt-3 border-b border-border overflow-x-auto">
        {GROUPS.map((group, i) => (
          <button
            key={group.label}
            onClick={() => setActiveGroup(i)}
            className={cn(
              "pb-2.5 text-sm font-medium transition-colors relative whitespace-nowrap",
              activeGroup === i
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {group.label}
            {activeGroup === i && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        ))}

        {/* Model used for every format generated from this panel */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-auto mb-2.5 flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
              {selectedModel.label}
              <ChevronDown className="w-3 h-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="text-xs">Image model</DropdownMenuLabel>
            {modelOptions.map((m) => (
              <DropdownMenuItem
                key={m.id}
                onClick={() => setImageModel(m.id)}
                className="gap-2 cursor-pointer items-start"
              >
                <span className="w-4 pt-0.5 flex-shrink-0">
                  {imageModel === m.id && <Check className="w-3.5 h-3.5" />}
                </span>
                <span className="flex flex-col">
                  <span className="text-sm">{m.label}</span>
                  <span className="text-[10px] text-muted-foreground leading-tight">{m.blurb}</span>
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Format cards */}
      <div className="p-4 overflow-y-auto max-h-[360px] min-h-[200px]">
        <div className="flex flex-wrap gap-3">
          {groupFormats.map((format) => {
            const variant = variants.get(format.ratio);
            const generatedUrl = variant?.publicUrl;
            const isGenerating = generating.has(format.ratio);
            const isSelected = selected.has(format.ratio);

            return (
              <div
                key={format.ratio}
                className={cn(
                  "flex-shrink-0 w-[190px] rounded-xl border bg-card overflow-hidden transition-all",
                  isSelected ? "border-primary/50 ring-1 ring-primary/20" : "border-border"
                )}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                  <span className="text-sm font-semibold text-foreground">{format.ratio}</span>
                  <div className="flex items-center gap-1">
                    {generatedUrl ? (
                      <div className="w-5 h-5 rounded bg-primary/20 flex items-center justify-center">
                        <svg className="w-3 h-3 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </div>
                    ) : (
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(format.ratio)}
                        className="w-4 h-4"
                      />
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="text-muted-foreground hover:text-foreground p-0.5">
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px] text-xs">
                        {format.description}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                {/* Preview */}
                <div className="px-3 py-2">
                  {generatedUrl ? (
                    <div
                      className="relative group/card cursor-pointer"
                      onClick={() => {
                        setLightboxUrl(generatedUrl);
                        setLightboxRatio(format.ratio);
                        setLightboxVariantId(variant?.id || null);
                        setLightboxFormat(format);
                      }}
                    >
                      <div
                        className="w-full rounded-lg overflow-hidden bg-muted"
                        style={{ aspectRatio: `${format.width}/${format.height}`, maxHeight: 160 }}
                      >
                        <img src={generatedUrl} alt={format.ratio} className="w-full h-full object-contain" />
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center gap-1.5 opacity-0 group-hover/card:opacity-100 transition-opacity">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="gap-1 h-7 text-xs bg-background/80"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLightboxUrl(generatedUrl);
                            setLightboxRatio(format.ratio);
                            setLightboxVariantId(variant?.id || null);
                            setLightboxFormat(format);
                          }}
                        >
                          <Maximize className="w-3 h-3" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="gap-1 h-7 text-xs bg-background/80"
                          onClick={(e) => { e.stopPropagation(); downloadVariant(format, generatedUrl); }}
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ) : isGenerating ? (
                    <div
                      className="w-full rounded-lg bg-muted/40 flex flex-col items-center justify-center gap-2"
                      style={{ aspectRatio: `${format.width || 16}/${format.height || 9}`, maxHeight: 160, minHeight: 80 }}
                    >
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <span className="text-[10px] text-muted-foreground">Generating...</span>
                    </div>
                  ) : (
                    <div
                      className="w-full rounded-lg bg-muted/40 flex flex-col items-center justify-center gap-1.5"
                      style={{ aspectRatio: `${format.width || 16}/${format.height || 9}`, maxHeight: 160, minHeight: 80 }}
                    >
                      <ImageIcon className="w-6 h-6 text-muted-foreground/40" />
                      <span className="text-[10px] text-muted-foreground">Not generated</span>
                      <Button
                        size="sm"
                        className="gap-1 h-7 text-xs mt-0.5"
                        onClick={() => handleGenerate(format)}
                        disabled={format.auto}
                      >
                        <Sparkles className="w-3 h-3" />
                        Generate
                      </Button>
                    </div>
                  )}
                </div>

                {/* Platforms */}
                <div className="px-3 pb-1.5">
                  <div className="flex flex-wrap gap-1">
                    {format.platforms.map((p) => (
                      <span key={p} className="text-[9px] bg-secondary text-muted-foreground px-1.5 py-0.5 rounded">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 border-t border-border text-[11px] text-muted-foreground">
                  <span>{format.auto ? "Auto" : isUpscaleFormat(format.ratio) ? `Up to ${format.width}px` : `${format.width} × ${format.height}`}</span>
                  <span>~{creditsFor(format.ratio)} credits</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox for editing generated variants */}
      {lightboxUrl && lightboxRatio && (
        <FormatEditLightbox
          isOpen={!!lightboxUrl}
          onClose={() => { setLightboxUrl(null); setLightboxRatio(null); setLightboxVariantId(null); setLightboxFormat(null); }}
          imageUrl={lightboxUrl}
          productImageUrls={productImageUrls}
          formatRatio={lightboxRatio || undefined}
          formatWidth={lightboxFormat?.width}
          formatHeight={lightboxFormat?.height}
          workspaceId={workspaceId}
          userId={userId}
          variantId={lightboxVariantId || undefined}
          onDelete={() => {
            if (lightboxRatio) handleDelete(lightboxRatio);
            setLightboxUrl(null);
            setLightboxRatio(null);
            setLightboxVariantId(null);
            setLightboxFormat(null);
          }}
          onImageEdited={(newUrl) => {
            if (lightboxRatio) {
              handleImageEdited(lightboxRatio, newUrl);
            }
            setLightboxUrl(newUrl);
          }}
        />
      )}
    </div>
  );
}
